import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import {
  FaBus,
  FaChevronDown,
  FaClock,
  FaCompress,
  FaExpand,
  FaExternalLinkAlt,
  FaLocationArrow,
  FaMapMarkedAlt,
  FaRedo,
  FaRoute,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import {
  formatLastUpdated,
  getTripId,
  normalizeLngLat,
  VIEW_MODES,
} from "../../../utils/tripUtils";
import "mapbox-gl/dist/mapbox-gl.css";
import "./MapHeroSection.css";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const escapeHtml = (value) =>
  String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const statusLabel = (trip) => {
  if (trip?.status === "delayed") return "Delayed";
  if (trip?.status === "boarding") return "Boarding";
  return "On time";
};

const statusClass = (trip) => {
  if (trip?.status === "delayed") return "delayed";
  if (trip?.status === "boarding") return "board";
  return "live";
};

const hasValidLngLat = (coord) =>
  Array.isArray(coord) &&
  coord.length === 2 &&
  Number.isFinite(Number(coord[0])) &&
  Number.isFinite(Number(coord[1]));

const fitMapToCoordinates = (map, coordinates, isMobile, extraPadding = {}) => {
  const validCoords = coordinates.filter(hasValidLngLat);
  if (!map || validCoords.length === 0) return;

  if (validCoords.length === 1) {
    map.easeTo({
      center: validCoords[0],
      zoom: 12.5,
      pitch: isMobile ? 30 : 45,
      duration: 900,
      essential: true,
    });
    return;
  }

  const bounds = validCoords.reduce(
    (nextBounds, coord) => nextBounds.extend(coord),
    new mapboxgl.LngLatBounds(validCoords[0], validCoords[0]),
  );

  map.fitBounds(bounds, {
    padding: isMobile
      ? { top: 110, bottom: 320, left: 34, right: 34, ...extraPadding }
      : { top: 120, bottom: 80, left: 470, right: 90, ...extraPadding },
    maxZoom: 13.5,
    duration: 1100,
    essential: true,
  });
};

const TripIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
    <path d="M8 6v6M16 6v6M2 12h19.6" />
    <path d="M18 18h3V9c0-1.7-1.3-3-3-3H6C4.3 6 3 7.3 3 9v9h3" />
    <circle cx="7" cy="18" r="1.7" />
    <circle cx="17" cy="18" r="1.7" />
  </svg>
);

const MapHeroSection = ({
  allTrips = [],
  visibleTrips = [],
  selectedTrip = null,
  selectedTripId = null,
  query,
  mode,
  socketState,
  socketError,
  apiError,
  lastSyncedAt,
  onSearchChange,
  onSelectTrip,
  onViewAll,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef(new Map());
  const endpointMarkersRef = useRef([]);
  const routeCacheRef = useRef(new Map());
  const routeDrawIdRef = useRef(0);
  const cameraActionRef = useRef(false);
  const userMovedRef = useRef(false);
  const lastFitKeyRef = useRef("");

  const [mapLoaded, setMapLoaded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [routeState, setRouteState] = useState({ status: "idle", error: null });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 760);
  const [draftQuery, setDraftQuery] = useState(query);
  const [searchExpanded, setSearchExpanded] = useState(false);

  useEffect(() => setDraftQuery(query), [query]);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 760);
      window.setTimeout(() => map.current?.resize(), 80);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const hasAppliedSearch =
    query?.from?.trim() || query?.to?.trim() || query?.text?.trim() || query?.type !== "all";

  const searchSummary = hasAppliedSearch
    ? [
        query.text,
        query.from && `from ${query.from}`,
        query.to && `to ${query.to}`,
        query.type && query.type !== "all" && query.type,
      ]
        .filter(Boolean)
        .join(" - ")
    : "Search routes";

  const selectedOrFirstTrip = selectedTrip || visibleTrips[0] || (hasAppliedSearch ? null : allTrips[0]);

  const modeLabel = useMemo(() => {
    if (mode === VIEW_MODES.DEDICATED_TRACKING) return "Dedicated tracking";
    if (mode === VIEW_MODES.SELECTED_TRIP) return "Selected trip";
    if (mode === VIEW_MODES.SEARCH_RESULTS) return "Search results";
    return "All live trips";
  }, [mode]);

  const connectionLabel = socketState === "tracking" ? "tracking" : socketState || "offline";

  const clearRoute = useCallback(() => {
    if (!map.current) return;
    ["selected-route-progress", "selected-route-line", "selected-route-glow"].forEach((id) => {
      if (map.current.getLayer(id)) map.current.removeLayer(id);
    });
    ["selected-route", "selected-route-progress-source"].forEach((id) => {
      if (map.current.getSource(id)) map.current.removeSource(id);
    });
    endpointMarkersRef.current.forEach((marker) => marker.remove());
    endpointMarkersRef.current.length = 0;
    setRouteState({ status: "idle", error: null });
  }, []);

  const setLineSource = useCallback((sourceId, data) => {
    if (!map.current) return;
    const existingSource = map.current.getSource(sourceId);
    if (existingSource?.setData) {
      existingSource.setData(data);
      return;
    }

    if (existingSource) {
      map.current.removeSource(sourceId);
    }

    map.current.addSource(sourceId, {
      type: "geojson",
      data,
    });
  }, []);

  const fetchRouteForTrip = useCallback(async (trip) => {
    const tripId = getTripId(trip);
    if (!tripId) return null;
    if (routeCacheRef.current.has(tripId)) return routeCacheRef.current.get(tripId);

    const fromTripStart = normalizeLngLat(trip.coordinateStart);
    const fromTripEnd = normalizeLngLat(trip.coordinateEnd);
    const fromTripVia = normalizeLngLat(trip.coordinateVia || trip.via);

    let start = fromTripStart;
    let end = fromTripEnd;
    let via = fromTripVia;

    if (!start || !end) {
      try {
        const response = await fetch(`${API_URL}/api/v1/himraahi/trip/Coords/${tripId}`);
        const payload = await response.json();
        start = start || normalizeLngLat(payload?.data?.coordinateStart);
        end = end || normalizeLngLat(payload?.data?.coordinateEnd);
        via = via || normalizeLngLat(payload?.data?.via);
      } catch {
        // Directions will fall back to known coordinates below.
      }
    }

    const current = trip.currentLocation?.coordinates;
    const waypoints = [start, current, via, end].filter(hasValidLngLat);
    if (waypoints.length < 2) return null;

    try {
      const waypointString = waypoints.map((coord) => `${coord[0]},${coord[1]}`).join(";");
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${waypointString}?geometries=geojson&overview=full&steps=true&access_token=${mapboxgl.accessToken}`,
      );
      const payload = await response.json();
      const route = payload?.routes?.[0];
      if (!route?.geometry?.coordinates?.length) return null;

      const routeData = {
        geometry: route.geometry,
        distance: route.distance,
        duration: route.duration,
        start: start || waypoints[0],
        end: end || waypoints[waypoints.length - 1],
        current,
      };
      routeCacheRef.current.set(tripId, routeData);
      return routeData;
    } catch {
      return null;
    }
  }, []);

  const drawRoute = useCallback(
    async (trip) => {
      if (!map.current || !mapLoaded || !trip) return;
      const drawId = routeDrawIdRef.current + 1;
      routeDrawIdRef.current = drawId;
      setRouteState({ status: "loading", error: null });
      clearRoute();

      const routeData = await fetchRouteForTrip(trip);
      if (drawId !== routeDrawIdRef.current || !map.current) return;

      if (!routeData) {
        setRouteState({ status: "error", error: "Route unavailable" });
        fitMapToCoordinates(map.current, [trip.currentLocation?.coordinates], isMobile);
        return;
      }

      clearRoute();

      setLineSource("selected-route", {
        type: "Feature",
        properties: {},
        geometry: routeData.geometry,
      });

      if (!map.current.getLayer("selected-route-glow")) {
        map.current.addLayer({
          id: "selected-route-glow",
          type: "line",
          source: "selected-route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#f48915",
            "line-width": 12,
            "line-opacity": 0.28,
            "line-blur": 7,
          },
        });
      }

      if (!map.current.getLayer("selected-route-line")) {
        map.current.addLayer({
          id: "selected-route-line",
          type: "line",
          source: "selected-route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#f48915",
            "line-width": 4,
            "line-opacity": 0.92,
          },
        });
      }

      const progressCoordinates = routeData.geometry.coordinates.slice(
        0,
        Math.max(2, Math.round(routeData.geometry.coordinates.length * 0.34)),
      );

      setLineSource("selected-route-progress-source", {
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: progressCoordinates },
      });

      if (!map.current.getLayer("selected-route-progress")) {
        map.current.addLayer({
          id: "selected-route-progress",
          type: "line",
          source: "selected-route-progress-source",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#5cb377",
            "line-width": 5,
            "line-opacity": 0.95,
          },
        });
      }

      [
        { coord: routeData.start, className: "route-endpoint route-start", label: trip.Start },
        { coord: routeData.end, className: "route-endpoint route-end", label: trip.End },
      ].forEach((point) => {
        if (!hasValidLngLat(point.coord)) return;
        const el = document.createElement("div");
        el.className = point.className;
        el.innerHTML = `<span></span><b>${escapeHtml(point.label)}</b>`;
        endpointMarkersRef.current.push(new mapboxgl.Marker(el).setLngLat(point.coord).addTo(map.current));
      });

      cameraActionRef.current = true;
      fitMapToCoordinates(
        map.current,
        [routeData.start, routeData.current, routeData.end],
        isMobile,
      );
      window.setTimeout(() => {
        cameraActionRef.current = false;
      }, 1300);

      setRouteState({ status: "ready", error: null });
    },
    [clearRoute, fetchRouteForTrip, isMobile, mapLoaded, setLineSource],
  );

  useEffect(() => {
    if (map.current || !mapContainer.current) return;
    const initialIsMobile = window.innerWidth <= 760;

    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/dark-v10",
      center: [77.1666, 31.9165],
      zoom: 7,
      pitch: 30,
      bearing: -8,
      attributionControl: false,
    });
    map.current = mapInstance;
    const markerStore = markersRef.current;
    const endpointStore = endpointMarkersRef.current;

    mapInstance.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-left");
    mapInstance.addControl(
      new mapboxgl.NavigationControl({ showCompass: true, showZoom: true }),
      initialIsMobile ? "bottom-right" : "top-right",
    );

    mapInstance.on("load", () => {
      setMapLoaded(true);
      const layers = mapInstance.getStyle().layers || [];
      const labelLayerId = layers.find(
        (layer) => layer.type === "symbol" && layer.layout?.["text-field"],
      )?.id;

      if (labelLayerId) {
        mapInstance.addLayer(
          {
            id: "rd-3d-buildings",
            source: "composite",
            "source-layer": "building",
            filter: ["==", "extrude", "true"],
            type: "fill-extrusion",
            minzoom: 14,
            paint: {
              "fill-extrusion-color": "#342b25",
              "fill-extrusion-height": ["get", "height"],
              "fill-extrusion-base": ["get", "min_height"],
              "fill-extrusion-opacity": 0.45,
            },
          },
          labelLayerId,
        );
      }
    });

    mapInstance.on("moveend", () => {
      if (!cameraActionRef.current) userMovedRef.current = true;
    });

    return () => {
      markerStore.forEach((marker) => marker.remove());
      markerStore.clear();
      endpointStore.forEach((marker) => marker.remove());
      endpointStore.length = 0;
      mapInstance.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const seen = new Set();

    visibleTrips.forEach((trip) => {
      const tripId = getTripId(trip);
      const coordinates = trip.currentLocation?.coordinates;
      if (!tripId || !hasValidLngLat(coordinates)) return;
      seen.add(tripId);

      const isSelected = selectedTripId === tripId;
      const existing = markersRef.current.get(tripId);

      if (existing) {
        existing.setLngLat(coordinates);
        const el = existing.getElement();
        el.classList.toggle("marker--selected", isSelected);
        el.dataset.status = statusClass(trip);
        return;
      }

      const el = document.createElement("button");
      el.type = "button";
      el.className = `marker ${isSelected ? "marker--selected" : ""}`;
      el.dataset.status = statusClass(trip);
      el.setAttribute("aria-label", `Track ${trip.Vehicle}`);
      el.innerHTML = `
        <span class="marker__ring"></span>
        <span class="marker__pill">
          <span class="marker__glyph">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M8 6v6M16 6v6M2 12h19.6"></path>
              <path d="M18 18h3V9c0-1.7-1.3-3-3-3H6C4.3 6 3 7.3 3 9v9h3"></path>
              <circle cx="7" cy="18" r="1.7"></circle>
              <circle cx="17" cy="18" r="1.7"></circle>
            </svg>
          </span>
          ${escapeHtml(trip.Vehicle)}
        </span>
      `;

      el.addEventListener("click", (event) => {
        event.preventDefault();
        onSelectTrip(tripId);
        setSheetOpen(true);
      });

      const popup = new mapboxgl.Popup({
        offset: 22,
        closeButton: false,
        closeOnClick: true,
        maxWidth: isMobile ? "280px" : "340px",
        className: "rd-map-popup",
      }).setHTML(`
        <div class="popup">
          <div class="popup__head">
            <span class="popup__glyph"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M8 6v6M16 6v6M2 12h19.6"></path><path d="M18 18h3V9c0-1.7-1.3-3-3-3H6C4.3 6 3 7.3 3 9v9h3"></path><circle cx="7" cy="18" r="1.7"></circle><circle cx="17" cy="18" r="1.7"></circle></svg></span>
            <div><div class="popup__route">${escapeHtml(trip.Start)} -> ${escapeHtml(trip.End)}</div><div class="popup__sub">${escapeHtml(trip.Vehicle)} - ${escapeHtml(trip.Type)}</div></div>
          </div>
          <div class="popup__stats">
            <div><span>ETA</span><b>${trip.etaMinutes || "--"}<small> min</small></b></div>
            <div><span>Speed</span><b>${Math.round(trip.speed || 0)}<small> km/h</small></b></div>
          </div>
        </div>
      `);

      const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat(coordinates)
        .setPopup(popup)
        .addTo(map.current);
      markersRef.current.set(tripId, marker);
    });

    markersRef.current.forEach((marker, tripId) => {
      if (!seen.has(tripId)) {
        marker.remove();
        markersRef.current.delete(tripId);
      }
    });
  }, [isMobile, mapLoaded, onSelectTrip, selectedTripId, visibleTrips]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const fitKey = `${mode}:${selectedTripId || "none"}:${visibleTrips.map((trip) => trip._id).join("|")}`;
    if (fitKey === lastFitKeyRef.current) return;
    lastFitKeyRef.current = fitKey;

    if (selectedTrip) {
      drawRoute(selectedTrip);
      return;
    }

    routeDrawIdRef.current += 1;
    clearRoute();
    if (userMovedRef.current && mode === VIEW_MODES.ALL_TRIPS) return;
    cameraActionRef.current = true;
    fitMapToCoordinates(
      map.current,
      visibleTrips.map((trip) => trip.currentLocation?.coordinates),
      isMobile,
    );
    window.setTimeout(() => {
      cameraActionRef.current = false;
    }, 1300);
  }, [clearRoute, drawRoute, isMobile, mapLoaded, mode, selectedTrip, selectedTripId, visibleTrips]);

  const submitSearch = (event) => {
    event.preventDefault();
    onSearchChange(draftQuery);
    userMovedRef.current = false;
    if (isMobile) setSearchExpanded(false);
  };

  const clearSearch = () => {
    const nextQuery = { from: "", to: "", text: "", type: "all" };
    setDraftQuery(nextQuery);
    onSearchChange(nextQuery);
    userMovedRef.current = false;
  };

  const recenter = () => {
    userMovedRef.current = false;
    cameraActionRef.current = true;
    fitMapToCoordinates(
      map.current,
      (selectedTrip ? [selectedTrip] : visibleTrips).map((trip) => trip.currentLocation?.coordinates),
      isMobile,
    );
    window.setTimeout(() => {
      cameraActionRef.current = false;
    }, 1200);
  };

  const toggleFullscreen = () => {
    const element = mapContainer.current?.parentElement;
    if (!document.fullscreenElement) {
      element?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
    window.setTimeout(() => map.current?.resize(), 120);
  };

  const activeCount = allTrips.length;
  const resultCount = visibleTrips.length;
  const feedError = socketError || (apiError && activeCount === 0);

  return (
    <section className={`map-hero-section ${isFullscreen ? "fullscreen" : ""}`}>
      <div ref={mapContainer} className="map-container" />
      <div className="map-vignette" />

      <div className="rd-map-topbar">
        <div className="brand-lockup">
          <span className="brand-mark"><FaBus /></span>
          <div>
            <strong>Rodhak</strong>
            <span>Live Map</span>
          </div>
        </div>
        <nav className="rd-map-nav" aria-label="Rodhak map navigation">
          <a href="/">Map</a>
          <a href="/vehicles">Trips</a>
          <a href="https://www.dndrodhak.in/about">About</a>
        </nav>
        <div className="topbar-metrics">
          <span className={`conn conn--${connectionLabel}`}>
            <i />
            {connectionLabel}
          </span>
          <span>{activeCount} trips</span>
        </div>
      </div>

      <aside
        className="panel"
        data-sheet={sheetOpen ? "open" : "peek"}
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        <button
          className="panel__handle"
          type="button"
          onClick={() => setSheetOpen((open) => !open)}
          aria-label="Toggle trip panel"
        >
          <span />
          <FaChevronDown />
        </button>

        <form
          className="trip-search"
          data-expanded={searchExpanded ? "true" : "false"}
          onSubmit={submitSearch}
        >
          <div className="panel__eyebrow rd-mono">
            <span>{modeLabel}</span>
            <span>{resultCount} visible</span>
          </div>

          <button
            className="search-toggle"
            type="button"
            aria-expanded={searchExpanded}
            onClick={() => setSearchExpanded((expanded) => !expanded)}
          >
            <span>
              <FaSearch />
              <b>{searchSummary}</b>
            </span>
            <FaChevronDown />
          </button>

          <div className="search-fields">
            <label className="search-field search-field--wide">
              <FaSearch />
              <input
                value={draftQuery.text}
                onChange={(event) =>
                  setDraftQuery((prev) => ({ ...prev, text: event.target.value }))
                }
                placeholder="Search bus, route, place"
              />
            </label>

            <div className="search-grid">
              <label className="search-field">
                <FaLocationArrow />
                <input
                  value={draftQuery.from}
                  onChange={(event) =>
                    setDraftQuery((prev) => ({ ...prev, from: event.target.value }))
                  }
                  placeholder="From"
                />
              </label>
              <label className="search-field">
                <FaMapMarkedAlt />
                <input
                  value={draftQuery.to}
                  onChange={(event) =>
                    setDraftQuery((prev) => ({ ...prev, to: event.target.value }))
                  }
                  placeholder="To"
                />
              </label>
            </div>

            <div className="filter-row">
              <select
                value={draftQuery.type}
                onChange={(event) =>
                  setDraftQuery((prev) => ({ ...prev, type: event.target.value }))
                }
              >
                <option value="all">All types</option>
                <option value="ordinary">Ordinary</option>
                <option value="deluxe">Deluxe</option>
                <option value="volvo">Volvo</option>
              </select>
              <button className="btn-search" type="submit">
                Search
              </button>
              <button className="btn-ghost" type="button" onClick={clearSearch}>
                <FaTimes />
              </button>
            </div>
          </div>
        </form>

        {selectedOrFirstTrip && (
          <div className="selected-card">
            <div className="selected-card__top">
              <span className={`status status--${statusClass(selectedOrFirstTrip)}`}>
                <i />
                {statusLabel(selectedOrFirstTrip)}
              </span>
              <span className="rd-mono">
                {formatLastUpdated(selectedOrFirstTrip.currentTime)}
              </span>
            </div>
            <h1 title={`${selectedOrFirstTrip.Start} to ${selectedOrFirstTrip.End}`}>
              {selectedOrFirstTrip.Start} <span>to</span> {selectedOrFirstTrip.End}
            </h1>
            <p title={`${selectedOrFirstTrip.Vehicle} - ${selectedOrFirstTrip.Type}`}>
              {selectedOrFirstTrip.Vehicle} - {selectedOrFirstTrip.Type}
            </p>
            <div className="selected-stats">
              <div>
                <span>ETA</span>
                <b>{selectedOrFirstTrip.etaMinutes || "--"}<small> min</small></b>
              </div>
              <div>
                <span>Speed</span>
                <b>{Math.round(selectedOrFirstTrip.speed || 0)}<small> km/h</small></b>
              </div>
              <div>
                <span>Route</span>
                <b>{routeState.status === "loading" ? "..." : routeState.error ? "N/A" : "Live"}</b>
              </div>
            </div>
            <div className="selected-actions">
              {selectedTrip ? (
                <button className="btn-ghost btn-ghost--wide" type="button" onClick={onViewAll}>
                  <FaRedo /> View all trips
                </button>
              ) : (
                <button
                  className="btn-ghost btn-ghost--wide"
                  type="button"
                  onClick={() => onSelectTrip(selectedOrFirstTrip._id)}
                >
                  <FaRoute /> Show route
                </button>
              )}
              <a className="btn-search btn-link" href={`/track/${selectedOrFirstTrip._id}`}>
                Track page <FaExternalLinkAlt />
              </a>
            </div>
          </div>
        )}

        <div className="triplist">
          {visibleTrips.length === 0 ? (
            <div className="empty-state">
              <FaBus />
              <h3>{hasAppliedSearch ? "No matching trips" : "No active trips"}</h3>
              <p>
                {hasAppliedSearch
                  ? `No active trip matches "${searchSummary}".`
                  : "Waiting for live driver coordinates from the backend."}
              </p>
              {hasAppliedSearch && (
                <button className="btn-search empty-state__action" type="button" onClick={clearSearch}>
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            visibleTrips.map((trip) => {
              const selected = selectedTripId === trip._id;
              return (
                <button
                  type="button"
                  className={`trip ${selected ? "is-selected" : ""}`}
                  data-status={statusClass(trip)}
                  key={trip._id}
                  onClick={() => onSelectTrip(trip._id)}
                >
                  <span className="trip__mark"><TripIcon /></span>
                  <span className="trip__body">
                    <span className="trip__route" title={`${trip.Start} to ${trip.End}`}>
                      {trip.Start} <span className="arr">-&gt;</span> {trip.End}
                    </span>
                    <span className="trip__meta">
                      <span className={`status status--${statusClass(trip)}`}>
                        <i />
                        {statusLabel(trip)}
                      </span>
                      <span>{trip.Vehicle}</span>
                    </span>
                  </span>
                  <span className="trip__right">
                    <span className="trip__eta">
                      {trip.etaMinutes || Math.max(1, Math.round((trip.speed || 30) / 2))}
                      <span> min</span>
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </aside>

      <div className="map-controls">
        <button type="button" onClick={recenter} aria-label="Recenter map">
          <FaLocationArrow />
        </button>
        <button type="button" onClick={toggleFullscreen} aria-label="Toggle fullscreen">
          {isFullscreen ? <FaCompress /> : <FaExpand />}
        </button>
      </div>

      <div className="map-status-card">
        <FaClock />
        <span>
          {feedError
            ? "Waiting for live backend data"
            : lastSyncedAt
            ? `Synced ${formatLastUpdated(lastSyncedAt)}`
            : "Waiting for live feed"}
        </span>
      </div>
    </section>
  );
};

export default MapHeroSection;
